'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { JSX, MouseEvent, ReactNode } from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { AuthHeaderSection } from '@/components/header/auth-header-section';
import { CategoryNav } from '@/components/header/category-nav';
import { SearchInputButton } from '@/components/header/search-input-button';
import { resolveHeaderScrollMaterialMixPercent } from '@/components/layout/app-header-scroll-material';
import { AppBanners } from '@/components/layout/demo-mode-banner';
import { isSimpleHeaderRoute } from '@/components/layout/simple-header-routes';
import { LiveNavigationLabel } from '@/components/live/live-navigation-label';
import { MorfiLogo, MorfiVortexLogoCopyMenu } from '@/components/logo';
import { MorfiProductButton } from '@/components/support-mainnet/landing-product-link';
import { LandingContentReveal, LandingSectionReveal } from '@/components/support-mainnet/landing-section-reveal';
import { AnimatedButton } from '@/components/ui/animated-button';
import { More01IconSolid } from '@/components/ui/icon/glyphs';
import { NavigationList, NavigationListItem } from '@/components/ui/navigation-list';
import { ProgressiveBlur } from '@/components/ui/progressive-blur';
import { categoryFilterSurface, desktopNavigationItems, isCategoryFilterListingSurface, isNavItemActive, navHrefWithCategory } from '@/config/navigation';
import { useLandingHeaderProductReveal } from '@/hooks/ui/use-landing-header-product-reveal';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { Link, usePathname } from '@/i18n/navigation'; // ARBITRARY: hash-only same-page section jumps on landing; network prefix does not apply
import { NetworkLink } from '@/i18n/network-link';
import { GUTTER_CLASS } from '@/lib/ui/layout';
import { elementIdFromHashHref, scrollToElementId } from '@/lib/ui/scroll-to-id';
import { cn } from '@/lib/ui/utils';
import { useLayoutStore } from '@/stores/layout';

/** Synced to `document.documentElement` for {@link AppShell} main-row top padding. */
const APP_HEADER_CHROME_HEIGHT_VAR = '--app-header-chrome-height' as const;

/** Liquid Glass–style frost (inline — matches liquid-glass-nav frost pipeline). */
const HEADER_GLASS_BACKDROP_FILTER = 'blur(20px) saturate(180%) brightness(1.05)';
function headerScrollMaterialBackground(scrollY: number): string {
  return `color-mix(in oklch, var(--background-secondary) ${resolveHeaderScrollMaterialMixPercent(scrollY)}%, transparent)`;
}

/** Full frost through the nav row; softer bottom feather (scroll-edge falloff). */
const HEADER_BACKDROP_MASK = '[mask-image:linear-gradient(to_bottom,black_0%,black_78%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_0%,black_78%,transparent_100%)]';
function HeaderScrollBackdrop(t0) {
  const $ = _c(5);
  const {
    scrollY
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("pointer-events-none absolute inset-0 z-0", "bg-background-secondary/88 supports-backdrop-filter:bg-transparent", HEADER_BACKDROP_MASK);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== scrollY) {
    t2 = headerScrollMaterialBackground(scrollY);
    $[1] = scrollY;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div aria-hidden="true" className={t1} style={{
      backdropFilter: HEADER_GLASS_BACKDROP_FILTER,
      WebkitBackdropFilter: HEADER_GLASS_BACKDROP_FILTER,
      backgroundColor: t2
    }} />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/** Landing nav links — 14px medium (design spec). */
const LANDING_NAV_LINK_CLASS = 'text-body font-medium text-foreground-secondary transition-colors hover:text-foreground-primary';
function onLandingSectionNavClick(event: MouseEvent<HTMLAnchorElement>): void {
  const href = event.currentTarget.getAttribute('href');
  if (href === null) {
    return;
  }
  const id = elementIdFromHashHref(href);
  if (id === null) {
    return;
  }
  event.preventDefault();
  scrollToElementId(id);
  window.history.pushState(null, '', `#${id}`); // ALLOW-NETWORK-UNAWARE: same-page hash sync; does not navigate.
}
function LandingSectionNavLink(t0) {
  const $ = _c(3);
  const {
    href,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== href) {
    t1 = <Link href={href} className={LANDING_NAV_LINK_CLASS} onClick={onLandingSectionNavClick}>{children}</Link>;
    $[0] = children;
    $[1] = href;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function LandingMarketingHeader(t0) {
  const $ = _c(35);
  const {
    headerRoot,
    setHeaderRoot
  } = t0;
  const tLanding = useTranslations("supportMainnet.landingHeader");
  const tHero = useTranslations("supportMainnet.hero");
  const productCtaRevealed = useLandingHeaderProductReveal(headerRoot);
  let t1;
  if ($[0] !== tHero) {
    t1 = tHero("launchCta");
    $[0] = tHero;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const productLabel = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ProgressiveBlur edge="top" tint={true} className="absolute inset-x-0 top-0 z-0" />;
    t3 = cn("relative z-10 pb-4", GUTTER_CLASS);
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <LandingSectionReveal className="shrink-0 justify-self-start" delay={0.05} trigger="mount"><MorfiVortexLogoCopyMenu size={32} /></LandingSectionReveal>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== tLanding) {
    t5 = tLanding("navigation");
    $[5] = tLanding;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== tLanding) {
    t6 = tLanding("about");
    $[7] = tLanding;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <LandingSectionReveal delay={0.1} trigger="mount"><LandingSectionNavLink href="#journey-mission-heading">{t6}</LandingSectionNavLink></LandingSectionReveal>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== tLanding) {
    t8 = tLanding("howItWorks");
    $[11] = tLanding;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8) {
    t9 = <LandingSectionReveal delay={0.15} trigger="mount"><LandingSectionNavLink href="#how-it-works-heading">{t8}</LandingSectionNavLink></LandingSectionReveal>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== tLanding) {
    t10 = tLanding("team");
    $[15] = tLanding;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10) {
    t11 = <LandingSectionReveal delay={0.2} trigger="mount"><LandingSectionNavLink href="#team">{t10}</LandingSectionNavLink></LandingSectionReveal>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t5 || $[21] !== t7 || $[22] !== t9) {
    t12 = <nav aria-label={t5} className="hidden items-center justify-center gap-8 md:flex md:justify-self-center">{t7}{t9}{t11}</nav>;
    $[19] = t11;
    $[20] = t5;
    $[21] = t7;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== productCtaRevealed || $[25] !== productLabel) {
    t13 = productCtaRevealed ? <LandingContentReveal revealed={productCtaRevealed} hideWhenHidden={true}><MorfiProductButton className="morfi-product-button--header" surface="header">{productLabel}</MorfiProductButton></LandingContentReveal> : null;
    $[24] = productCtaRevealed;
    $[25] = productLabel;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13) {
    t14 = <LandingSectionReveal className="shrink-0 justify-self-end" delay={0.25} trigger="mount">{t13}</LandingSectionReveal>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t12 || $[30] !== t14) {
    t15 = <div className={t3}><div className="grid h-16 grid-cols-[1fr_auto] items-center gap-4 md:grid-cols-[1fr_auto_1fr]">{t4}{t12}{t14}</div></div>;
    $[29] = t12;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== setHeaderRoot || $[33] !== t15) {
    t16 = <header ref={setHeaderRoot} className="fixed inset-x-0 top-0 z-sticky overflow-visible bg-transparent">{t2}{t15}</header>;
    $[32] = setHeaderRoot;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
}
function AppLogoLink() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <NetworkLink href="/markets" className="flex shrink-0 items-center"><MorfiLogo size={32} mark={true} className="sm:hidden" /><MorfiLogo size={32} className="hidden sm:block" /></NetworkLink>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function AppHeader() {
  const $ = _c(56);
  const pathname = usePathname();
  const simpleHeader = isSimpleHeaderRoute(pathname);
  let t0;
  if ($[0] !== pathname) {
    t0 = categoryFilterSurface(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const categorySurface = t0;
  const hasCategoryActions = categorySurface !== null;
  const activeCategory = useSearchParams().get("category");
  const setMobileMenuOpen = useLayoutStore(_temp);
  const setCategoryActionsElement = useLayoutStore(_temp2);
  const t = useTranslations("header");
  const tNav = useTranslations("nav");
  const liveEventsEnabled = useFeatureGate("live_events") === true;
  const desktopItems = desktopNavigationItems(liveEventsEnabled);
  const [scrollY, setScrollY] = useState(0);
  const [headerRoot, setHeaderRoot] = useState(null);
  let t1;
  let t2;
  if ($[2] !== headerRoot) {
    t1 = () => {
      const el = headerRoot;
      if (el === null) {
        return;
      }
      const root = document.documentElement;
      const syncHeight = () => {
        root.style.setProperty(APP_HEADER_CHROME_HEIGHT_VAR, `${el.getBoundingClientRect().height}px`);
      };
      const ro = new ResizeObserver(syncHeight);
      ro.observe(el);
      syncHeight();
      return () => {
        ro.disconnect();
        root.style.removeProperty(APP_HEADER_CHROME_HEIGHT_VAR);
      };
    };
    t2 = [headerRoot];
    $[2] = headerRoot;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useLayoutEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const updateScrolled = function updateScrolled() {
        setScrollY(window.scrollY);
      };
      updateScrolled();
      window.addEventListener("scroll", updateScrolled, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", updateScrolled);
      };
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  if (simpleHeader) {
    let t5;
    if ($[7] !== headerRoot || $[8] !== setHeaderRoot) {
      t5 = <LandingMarketingHeader headerRoot={headerRoot} setHeaderRoot={setHeaderRoot} />;
      $[7] = headerRoot;
      $[8] = setHeaderRoot;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  const t5 = "fixed inset-x-0 top-0 z-sticky overflow-hidden bg-transparent";
  let t6;
  if ($[10] !== scrollY) {
    t6 = <HeaderScrollBackdrop scrollY={scrollY} />;
    $[10] = scrollY;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <AppBanners />;
    t8 = cn("relative z-10 flex h-16 min-w-0 items-center", GUTTER_CLASS);
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  const t9 = "flex shrink-0 items-center gap-4";
  let t10;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <AppLogoLink />;
    t11 = <div className="h-5 w-px bg-border-primary hidden lg:block" />;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  const t12 = "hidden shrink-0 lg:block";
  const T0 = NavigationList;
  let t13;
  if ($[16] !== t) {
    t13 = t("navigation");
    $[16] = t;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = "none";
  const t15 = desktopItems.map(item => {
    const isActive = isNavItemActive(item, pathname);
    const label = item.key.replace("nav.", "") as keyof IntlMessages['nav'];
    return <NavigationListItem key={item.key} href={navHrefWithCategory(item.href, activeCategory)} active={isActive}>{item.key === "nav.live" ? <LiveNavigationLabel>{tNav(label)}</LiveNavigationLabel> : tNav(label)}</NavigationListItem>;
  });
  let t16;
  if ($[18] !== T0 || $[19] !== t13 || $[20] !== t15) {
    t16 = <div className={t12}><T0 aria-label={t13} edgeEffect={t14}>{t15}</T0></div>;
    $[18] = T0;
    $[19] = t13;
    $[20] = t15;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t16) {
    t17 = <div className={t9}>{t10}{t11}{t16}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  let t19;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SearchInputButton />;
    t19 = <AuthHeaderSection />;
    $[26] = t18;
    $[27] = t19;
  } else {
    t18 = $[26];
    t19 = $[27];
  }
  let t20;
  if ($[28] !== setMobileMenuOpen) {
    t20 = () => setMobileMenuOpen(true);
    $[28] = setMobileMenuOpen;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t) {
    t21 = t("menu");
    $[30] = t;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== t20 || $[33] !== t21) {
    t22 = <div className="flex min-w-0 flex-1 items-center justify-end gap-1 sm:gap-2">{t18}{t19}<AnimatedButton variant="tertiary" size="sm" prefix={More01IconSolid} onClick={t20} aria-label={t21} /></div>;
    $[32] = t20;
    $[33] = t21;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== t17 || $[36] !== t22 || $[37] !== t8) {
    t23 = <div className={t8}>{t17}{t22}</div>;
    $[35] = t17;
    $[36] = t22;
    $[37] = t8;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== pathname) {
    t24 = !isCategoryFilterListingSurface(pathname) && "hidden lg:flex";
    $[39] = pathname;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== t24) {
    t25 = cn("relative z-10 pb-4 pt-1 lg:flex lg:h-14 lg:items-center lg:gap-6", GUTTER_CLASS, "px-0 sm:px-0", t24);
    $[41] = t24;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="min-w-0 flex-1"><CategoryNav /></div>;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== hasCategoryActions || $[45] !== setCategoryActionsElement) {
    t27 = hasCategoryActions && <div ref={setCategoryActionsElement} className="hidden shrink-0 lg:block [&:has(>[data-loading])>div:not([data-loading])]:hidden [&>[data-loading]:has(~[data-loading])]:hidden" />;
    $[44] = hasCategoryActions;
    $[45] = setCategoryActionsElement;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== t25 || $[48] !== t27) {
    t28 = <div className={t25}>{t26}{t27}</div>;
    $[47] = t25;
    $[48] = t27;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== setHeaderRoot || $[51] !== t23 || $[52] !== t28 || $[53] !== t6 || $[54] !== t7) {
    t29 = <header ref={setHeaderRoot} className={t5}>{t6}{t7}{t23}{t28}</header>;
    $[50] = setHeaderRoot;
    $[51] = t23;
    $[52] = t28;
    $[53] = t6;
    $[54] = t7;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  return t29;
}
function _temp2(state_0) {
  return state_0.setCategoryActionsElement;
}
function _temp(state) {
  return state.setMobileMenuOpen;
}